import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip, buffer, filter,debounce } from 'rxjs/operators';

import { NSendComponentModel } from './send.component.model';

import { ITag } from 'src/app/shared/models/paper.model';

import { SignStatusEnum } from 'src/app/shared/enum/signStatusEnum';

import { PaperHttpService } from 'src/app/shared/services/paper/paper.http.service';
import { LoadingService } from 'src/app/shared/components/settings/loading/loading.service';
import { ToasterService } from 'src/app/shared/components/toaster/toaster.service';

import { ErrorUtil } from 'src/app/shared/utils/Error-util';

const LIMIT_POR_PAGINA = 100;

@Injectable()
export class SendComponentStorageService {
  public naoAssinadoPapers: BehaviorSubject<NSendComponentModel.Paper[]> = new BehaviorSubject<NSendComponentModel.Paper[]>([]);
  private naoAssinadoLastId: number | null = null;
  private naoAssinadoUltimaPagina: boolean = false;

  public parcialmenteAssinadoPapers: BehaviorSubject<NSendComponentModel.Paper[]> = new BehaviorSubject<NSendComponentModel.Paper[]>([]);
  private parcialmenteAssinadoLastId: number | null = null;
  private parcialmenteAssinadoUltimaPagina: boolean = false;

  public totalmenteAssinadoPapers: BehaviorSubject<NSendComponentModel.Paper[]> = new BehaviorSubject<NSendComponentModel.Paper[]>([]);
  private totalmenteAssinadoLastId: number | null = null;
  private totalmenteAssinadoUltimaPagina: boolean = false;

  public filtroTag: BehaviorSubject<ITag[]> = new BehaviorSubject<ITag[]>([]);
  private filtroTag$!: Subscription;

  public filtroPesquisa: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private filtroPesquisa$!: Subscription;

  private naoAssinadoCarregarDadosEvent = new Subject<void>();
  private naoAssinadoCarregarDadosEvent$!: Subscription;

  private parcialmenteAssinadoCarregarDadosEvent = new Subject<void>();
  private parcialmenteAssinadoCarregarDadosEvent$!: Subscription;

  private totalmenteAssinadoCarregarDadosEvent = new Subject<void>();
  private totalmenteAssinadoCarregarDadosEvent$!: Subscription;

  public removerTagPaperEvent = new Subject<{ signStatusEnum: SignStatusEnum, paperId: number, tagId: number[] }>();
  private removerTagPaperEvent$!: Subscription;

  public sincronizarTagPaperEvent = new Subject<{ signStatusEnum: SignStatusEnum, paperId: number, tags: ITag[] }>();
  private sincronizarTagPaperEvent$!: Subscription;

  public deletePapersEvent = new Subject<{ signStatusEnum: SignStatusEnum, paperId: number[] }>();
  private deletePapersEvent$!: Subscription;

  constructor(
    private paperHttpService: PaperHttpService,
    private loadingService: LoadingService,
    private toasterService: ToasterService,
  ) { }

  public async init(): Promise<void> {
    this.initSubscribers();
    this.requestHandler(this.carregarTodos);
  }

  public destroy(): void {
    this.filtroTag$.unsubscribe();
    this.filtroPesquisa$.unsubscribe();
    this.naoAssinadoCarregarDadosEvent$.unsubscribe();
    this.parcialmenteAssinadoCarregarDadosEvent$.unsubscribe();
    this.totalmenteAssinadoCarregarDadosEvent$.unsubscribe();
    this.removerTagPaperEvent$.unsubscribe();
    this.sincronizarTagPaperEvent$.unsubscribe();
    this.deletePapersEvent$.unsubscribe();

    this.filtroTag.next([]);
    this.filtroPesquisa.next(null);
    this.cleanAll()
  }

  private initSubscribers(): void {
    this.filtroTag$ = this.filtroTag.pipe(
      skip(1), // Quando e feito o subscribe ele faz um primeiro emit, entao ele pula esse emit.
      debounceTime(100),
      distinctUntilChanged(),
    ).subscribe(async () => {
      this.cleanAll();
      this.requestHandler(this.carregarTodos);
    });

    this.filtroPesquisa$ = this.filtroPesquisa.pipe(
      skip(1), // Quando e feito o subscribe ele faz um primeiro emit, entao ele pula esse emit.
      debounceTime(400),
      distinctUntilChanged(),
    ).subscribe(async () => {
      this.cleanAll();
      this.requestHandler(this.carregarTodos);
    });

    this.removerTagPaperEvent$ = this.removerTagPaperEvent.pipe(
      debounceTime(300),
      distinctUntilChanged(),
    ).subscribe(async ({ signStatusEnum, paperId, tagId }) => {
      this.requestHandler(this.excluirTagsPaper, signStatusEnum, paperId, tagId);
    });

    this.naoAssinadoCarregarDadosEvent$ = this.naoAssinadoCarregarDadosEvent.pipe(
      buffer(this.naoAssinadoCarregarDadosEvent.pipe(debounceTime(100))),
      filter(pBuffer => pBuffer.length > 0),
    ).subscribe(async () => {
      this.requestHandler(this.naoAssinadoCarregarPaper, true);
    });

    this.parcialmenteAssinadoCarregarDadosEvent$ = this.parcialmenteAssinadoCarregarDadosEvent.pipe(
      buffer(this.parcialmenteAssinadoCarregarDadosEvent.pipe(debounceTime(100))),
      filter(pBuffer => pBuffer.length > 0),
    ).subscribe(async () => {
      this.requestHandler(this.parcialmenteAssinadoCarregarPaper, true);
    });

    this.totalmenteAssinadoCarregarDadosEvent$ = this.totalmenteAssinadoCarregarDadosEvent.pipe(
      buffer(this.totalmenteAssinadoCarregarDadosEvent.pipe(debounceTime(100))),
      filter(pBuffer => pBuffer.length > 0),
    ).subscribe(async () => {
      this.requestHandler(this.totalmenteAssinadoCarregarPaper, true);
    });

    this.sincronizarTagPaperEvent$ = this.sincronizarTagPaperEvent.pipe(
      debounceTime(100),
      distinctUntilChanged(),
    ).subscribe(async ({ signStatusEnum, paperId, tags }) => {
      this.requestHandler(this.sincronizarTagsPaper, signStatusEnum, paperId, tags);
    });

    this.deletePapersEvent$ = this.deletePapersEvent.pipe(
      debounceTime(100),
      distinctUntilChanged(),
    ).subscribe(async ({ signStatusEnum, paperId }) => {
      this.requestHandler(this.excluirPaper, signStatusEnum, paperId);
    });
  }

  public cleanAll(): void {
    this.clearNaoAssinado();
    this.clearParcialmenteAssinado();
    this.clearTotalmenteAssinado();
  }

  public clearNaoAssinado(): void {
    this.naoAssinadoLastId = null;
    this.naoAssinadoUltimaPagina = false;
    this.naoAssinadoPapers.next([]);
  }

  public clearTotalmenteAssinado(): void {
    this.totalmenteAssinadoLastId = null;
    this.totalmenteAssinadoUltimaPagina = false;
    this.totalmenteAssinadoPapers.next([]);
  }

  public clearParcialmenteAssinado(): void {
    this.parcialmenteAssinadoLastId = null;
    this.parcialmenteAssinadoUltimaPagina = false;
    this.parcialmenteAssinadoPapers.next([]);
  }

  public naoAssinadoProximaPagina(): void {
    this.naoAssinadoCarregarDadosEvent.next();
  }

  public parcialmenteAssinadoProximaPagina(): void {
    this.parcialmenteAssinadoCarregarDadosEvent.next();
  }

  public totalmenteAssinadoProximaPagina(): void {
    this.totalmenteAssinadoCarregarDadosEvent.next();
  }

  //#region Actions
  private async carregarTodos(): Promise<void> {

    this.naoAssinadoCarregarDadosEvent.next();
    this.parcialmenteAssinadoCarregarDadosEvent.next();
    this.totalmenteAssinadoCarregarDadosEvent.next();
  }

  private async naoAssinadoCarregarPaper(pIncrementarDados: boolean): Promise<void> {
    if (this.naoAssinadoUltimaPagina === true) {
      return;
    }
    const response = await this.carregarPapers(SignStatusEnum.naoAssinado, this.naoAssinadoLastId, this.filtroTag.value, this.filtroPesquisa.value);
    this.naoAssinadoLastId = response.ultimoPaperId ?? this.naoAssinadoLastId;
    this.naoAssinadoUltimaPagina = response.isUltimaPagina;
    if (pIncrementarDados === true) {
      this.naoAssinadoPapers.next([...this.naoAssinadoPapers.value, ...response.papers]);
    } else {
      this.naoAssinadoPapers.next([...response.papers]);
    }
  }


  private async parcialmenteAssinadoCarregarPaper(pIncrementarDados: boolean): Promise<void> {
    if (this.parcialmenteAssinadoUltimaPagina === true) {
      return;
    }

    const response = await this.carregarPapers(SignStatusEnum.parcialmenteAssinado, this.parcialmenteAssinadoLastId, this.filtroTag.value, this.filtroPesquisa.value);
    this.parcialmenteAssinadoLastId = response.ultimoPaperId ?? this.parcialmenteAssinadoLastId;
    this.parcialmenteAssinadoUltimaPagina = response.isUltimaPagina;
    if (pIncrementarDados === true) {
      this.parcialmenteAssinadoPapers.next([...this.parcialmenteAssinadoPapers.value, ...response.papers]);
    } else {
      this.parcialmenteAssinadoPapers.next([...response.papers]);
    }
  }

  private async totalmenteAssinadoCarregarPaper(pIncrementarDados: boolean): Promise<void> {
    if (this.totalmenteAssinadoUltimaPagina === true) {
      return;
    }
    const response = await this.carregarPapers(SignStatusEnum.totalmenteAssinado, this.totalmenteAssinadoLastId, this.filtroTag.value, this.filtroPesquisa.value);
    this.totalmenteAssinadoLastId = response.ultimoPaperId ?? this.totalmenteAssinadoLastId;
    this.totalmenteAssinadoUltimaPagina = response.isUltimaPagina;
    if (pIncrementarDados === true) {
      this.totalmenteAssinadoPapers.next([...this.totalmenteAssinadoPapers.value, ...response.papers]);
    } else {
      this.totalmenteAssinadoPapers.next([...response.papers]);
    }
  }

  private async carregarPapers(pTipoSituacaoEvento: SignStatusEnum, pUltimoId: number | null, pTags: ITag[], pFiltroGenerico: string | null): Promise<ICarregarPapersReturn> {
    const papersDb = await this.paperHttpService.getPapersByStatus(pTipoSituacaoEvento, pUltimoId, pTags, pFiltroGenerico);
    let isUltimaPagina: boolean = false;
    let ultimoPaperId: number | null = null;

    if (papersDb.papers.length < LIMIT_POR_PAGINA) {
      isUltimaPagina = true;
    } else {
      ultimoPaperId = papersDb.papers[papersDb.papers.length - 1].paperId;
    }

    return {
      papers: papersDb.papers.map(pPaper => new NSendComponentModel.Paper(pPaper)),
      isUltimaPagina,
      ultimoPaperId,
    }
  }

  private async excluirTagsPaper(pSituacao: SignStatusEnum, pPaperId: number, pTagsRemover: number[]): Promise<void> {
    const response = await this.paperHttpService.deleteTagMassaPaper({ paperId: pPaperId, tagsId: pTagsRemover });

    if (response.success === false) {
      throw new Error(response.message);
    }

    let paper: NSendComponentModel.Paper | undefined = undefined;
    switch (pSituacao) {
      case SignStatusEnum.naoAssinado:
        paper = this.naoAssinadoPapers.value.find(pPaper => pPaper.id === pPaperId);
        break;
      case SignStatusEnum.parcialmenteAssinado:
        paper = this.parcialmenteAssinadoPapers.value.find(pPaper => pPaper.id === pPaperId);
        break;
      case SignStatusEnum.totalmenteAssinado:
        paper = this.totalmenteAssinadoPapers.value.find(pPaper => pPaper.id === pPaperId);
        break;
    }

    if (paper !== undefined) {
      paper.tags = paper.tags.filter(pTag => !pTagsRemover.includes(pTag.id));
    }
  }

  private async sincronizarTagsPaper(pSituacao: SignStatusEnum, pPaperId: number, pTags: ITag[]): Promise<void> {
    const idTagsInicos = [...new Set(pTags.map(pTag => pTag.tagId))];
    const response = await this.paperHttpService.sincronizarTagMassaPaper({ paperId: pPaperId, tagsId: idTagsInicos });
    if (response.success === false) {
      throw new Error(response.message);
    }

    let paper: NSendComponentModel.Paper | undefined = undefined;
    switch (pSituacao) {
      case SignStatusEnum.naoAssinado:
        paper = this.naoAssinadoPapers.value.find(pPaper => pPaper.id === pPaperId);
        break;
      case SignStatusEnum.parcialmenteAssinado:
        paper = this.parcialmenteAssinadoPapers.value.find(pPaper => pPaper.id === pPaperId);
        break;
      case SignStatusEnum.totalmenteAssinado:
        paper = this.totalmenteAssinadoPapers.value.find(pPaper => pPaper.id === pPaperId);
        break;
    }

    if (paper !== undefined) {
      const tagsRemover = response.dados.removidos.map(pTag => pTag.id);
      paper.tags = paper.tags.filter(pTag => !tagsRemover.includes(pTag.id));
      paper.tags = [...paper.tags, ...response.dados.criados];
    }
  }

  private async excluirPaper(pSituacao: SignStatusEnum, pPaperId: number[]): Promise<void> {
    const response = await this.paperHttpService.deletePaperMassa({ paperId: pPaperId });

    if (response.success === false) {
      throw new Error(response.message);
    }

    switch (pSituacao) {
      case SignStatusEnum.naoAssinado:
        this.naoAssinadoPapers.next(this.naoAssinadoPapers.value.filter(pPaper => pPaperId.includes(pPaper.id) === false));
        break;
      case SignStatusEnum.parcialmenteAssinado:
        this.parcialmenteAssinadoPapers.next(this.parcialmenteAssinadoPapers.value.filter(pPaper => pPaperId.includes(pPaper.id) === false));
        break;
      case SignStatusEnum.totalmenteAssinado:
        this.totalmenteAssinadoPapers.next(this.totalmenteAssinadoPapers.value.filter(pPaper => pPaperId.includes(pPaper.id) === false));
        break;
    }
  }
  //#endregion Actions

  /**
   * Utilitario para realizar e tratar erros das requisições;
   *
   * TODO: Verificar se e possivel fazer ele em utilitário generico.
   * @param pFunction Funcão a ser executada
   * @param args Parametros da funcao
   */
  private async requestHandler(pFunction: any, ...args: any[]): Promise<void> {
    try {
      this.loadingService.showLoader();
      await pFunction.call(this, ...args);
      this.loadingService.hideLoader();
    } catch (error) {
      console.error(error);
      this.toasterService.showError(ErrorUtil.getErrorMensagem(error, 'Erro ao carregar os documentos'));
      this.loadingService.hideLoader();
    }
  }
}

interface ICarregarPapersReturn {
  papers: NSendComponentModel.Paper[];
  isUltimaPagina: boolean;
  ultimoPaperId: number | null;
}
